import { useMemo } from "react";
import { analyzeAts, type AtsStatus } from "../lib/ats";
import { Modal } from "./Modal";

// ATS verification panel. Re-runs the (pure, client-side) analysis on the
// current Typst source every time it opens or the source changes. Nothing
// leaves the browser — it only reads the string already in memory.

const STATUS_ICON: Record<AtsStatus, string> = {
  pass: "✓",
  warn: "!",
  fail: "×",
  info: "·",
};
const STATUS_CLASS: Record<AtsStatus, string> = {
  pass: "text-success border-success/50",
  warn: "text-accent border-accent/50",
  fail: "text-danger border-danger/50",
  info: "text-mute border-line",
};

function gradeColor(grade: string): string {
  if (grade === "A") return "text-success";
  if (grade === "B") return "text-accent";
  if (grade === "C") return "text-accent";
  return "text-danger";
}

export function AtsPanel({ source, onClose }: { source: string; onClose: () => void }) {
  const report = useMemo(() => analyzeAts(source), [source]);

  const ring =
    report.score >= 90
      ? "#22c55e"
      : report.score >= 55
        ? "#e0322f"
        : "#ef4444";

  return (
    <Modal ariaLabel="Verificación ATS" icon="✓" title="verificación ats" onClose={onClose}>
      <div className="space-y-5 p-5">
          {/* Score header */}
          <section className="flex flex-wrap items-center gap-5">
            <div
              className="relative flex h-24 w-24 shrink-0 items-center justify-center rounded-full"
              style={{
                background: `conic-gradient(${ring} ${report.score * 3.6}deg, #2a2a2e 0deg)`,
              }}
              aria-hidden="true"
            >
              <div className="flex h-[76px] w-[76px] flex-col items-center justify-center rounded-full bg-panel">
                <span className={`font-mono text-2xl font-bold ${gradeColor(report.grade)}`}>
                  {report.score}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-mute">
                  / 100
                </span>
              </div>
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <div className="font-mono text-sm text-ink">
                Nota ATS:{" "}
                <span className={`font-bold ${gradeColor(report.grade)}`}>{report.grade}</span>
              </div>
              <div className="flex flex-wrap gap-3 font-mono text-xs text-mute">
                <span className="text-success">✓ {report.counts.pass} ok</span>
                <span className="text-accent">! {report.counts.warn} avisos</span>
                <span className="text-danger">× {report.counts.fail} fallos</span>
                <span>· {report.wordCount} palabras</span>
              </div>
              <p className="text-[11px] leading-relaxed text-mute">
                Análisis 100% en tu navegador sobre el texto del editor. Nada se sube.
                Es una guía heurística — cada ATS parsea distinto.
              </p>
            </div>
          </section>

          {/* Categories */}
          {report.categories.map((cat) => (
            <section key={cat.id} className="space-y-2">
              <h2 className="font-mono text-xs uppercase tracking-widest text-accent">
                # {cat.title}
              </h2>
              <ul className="space-y-1.5">
                {cat.checks.map((ch) => (
                  <li
                    key={ch.id}
                    className="flex gap-3 rounded border border-line bg-bg/40 p-2.5"
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border font-mono text-xs ${STATUS_CLASS[ch.status]}`}
                      aria-hidden="true"
                    >
                      {STATUS_ICON[ch.status]}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-sm text-ink">{ch.label}</div>
                      <div className="text-xs text-mute">{ch.detail}</div>
                      {ch.hint && (ch.status === "warn" || ch.status === "fail") && (
                        <div className="mt-1 text-xs text-accent">→ {ch.hint}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
      </div>
    </Modal>
  );
}
